<script lang="ts">
  import { get } from 'lodash';
  import Page from '../Page.svelte';
  import Scroller from '../Scroller.svelte';
  import {
    Button,
    Dialog,
    Icon,
    List,
    ListItem,
    DataTable,
    DataTableCell,
    DataTableHead,
    DataTableRow,
    DataTableBody,
    Switch,
  } from 'svelte-materialify';
  import ItemEdit from '../item/ItemEdit.svelte';
  import type {
    GetListParams,
    Item,
    ItemActionEvent,
  } from '../../modules/item/item.interface';
  import { ItemService, ProjectConfigService } from '../../modules';
  import { wrapProgress } from '../../store';
  import { ItemStatus } from '../../modules/item/item.constants';
  import type {
    CategoryConfig,
    CategoryPropConfig,
  } from '../../modules/projects/project.interface';
  import FeaturedListItem from '../item/FeaturedListItem.svelte';

  export let params: { category: string; project: string };

  let enabledItems: Item[] = [];
  let disabledItems: Item[] = [];
  let editDialogActive = false;
  let isEdit = false;
  let itemOnEdit: Item;
  let resetScroll: () => void;

  $: project = params.project;
  $: projectConfig = ProjectConfigService.get(project);
  $: category = projectConfig.featuredCategories[0]?.key;
  $: categoryConfig = ProjectConfigService.getCategoryConfig(
    projectConfig.key,
    category,
    true
  );
  $: onCategoryChange(category);

  function onCategoryChange(_: string) {
    fetchListItems(ItemStatus.FEATURED);
    fetchListItems(ItemStatus.FEATURED_DISABLED);
  }

  function toggleDialog() {
    editDialogActive = !editDialogActive;
  }

  function createNewItem() {
    openItemEdit(ItemService.createDefaultFeaturedItem(project, category), false);
  }

  function openItemEdit(item: Item, _isEdit: boolean) {
    itemOnEdit = item;
    isEdit = _isEdit;
    toggleDialog();
  }

  async function onItemCreate(evt: ItemActionEvent) {
    const { item, files } = evt.detail;
    await ItemService.create(item, files.get());

    toggleDialog();
    fetchListItems(item.status);
  }

  async function onItemUpdate(evt: ItemActionEvent) {
    const { item, files } = evt.detail;
    await ItemService.update(item, files.get());

    toggleDialog();
    fetchListItems(item.status);
  }

  async function updateItemStatus(item: Item, status: ItemStatus) {
    return wrapProgress(async () => {
      await ItemService.updateStatus(item.id, status);
      fetchListItems(ItemStatus.FEATURED);
      fetchListItems(ItemStatus.FEATURED_DISABLED);
    })();
  }

  async function fetchListItems(status: ItemStatus) {
    return wrapProgress(async () => {
      const params: GetListParams = {
        page: 1,
        limit: 50,
        status,
        category,
        project,
      };

      const { docs } = await ItemService.getList(params);

      if (status === ItemStatus.FEATURED) {
        enabledItems = docs;
      } else {
        disabledItems = docs;
      }

      resetScroll();
    })();
  }

  function openCategory(categoryConfig: CategoryConfig) {
    category = categoryConfig.key;
  }

  function getItemPropValue(
    item: Item,
    categoryPropConfig: CategoryPropConfig,
    isDetails = false
  ) {
    const key = isDetails ? `details.${categoryPropConfig.key}` : categoryPropConfig.key;

    return get(item, key);
  }
</script>

{#if projectConfig}
  <Page title="Featured Items">
    <div slot="actions">
      <Button class="ml-2 green white-text" on:click={() => createNewItem()}
        >Create New</Button
      >
    </div>
    <div slot="sidebar">
      <Scroller>
        <List class="elevation-2">
          {#each projectConfig.featuredCategories as featuredCategory}
            <ListItem
              active={featuredCategory.key === category}
              on:click={() => openCategory(featuredCategory)}
              >{featuredCategory.name}</ListItem
            >
          {/each}
        </List>
      </Scroller>
    </div>
    <Scroller bind:resetScroll>
      <DataTable>
        <DataTableHead>
          <DataTableRow>
            {#each categoryConfig.props as categoryProp}
              <DataTableCell>{categoryProp.name}</DataTableCell>
            {/each}
            {#each categoryConfig.details as detailProp}
              <DataTableCell>{detailProp.name}</DataTableCell>
            {/each}
            <DataTableCell>Enabled</DataTableCell>
            <DataTableCell>Edit</DataTableCell>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {#each enabledItems as item (item.id)}
            <DataTableRow>
              {#each categoryConfig.props as categoryProp}
                <FeaturedListItem
                  {categoryProp}
                  value={getItemPropValue(item, categoryProp)}
                />
              {/each}
              {#each categoryConfig.details as categoryProp}
                <FeaturedListItem
                  {categoryProp}
                  value={getItemPropValue(item, categoryProp, true)}
                />
              {/each}
              <DataTableCell
                ><Switch
                  checked={true}
                  on:change={() => updateItemStatus(item, ItemStatus.FEATURED_DISABLED)}
                /></DataTableCell
              >
              <DataTableCell>
                <Button icon on:click={() => openItemEdit(item, true)}>
                  <Icon class="mdi mdi-pencil" />
                </Button>
              </DataTableCell>
            </DataTableRow>
          {/each}
          {#each disabledItems as item (item.id)}
            <DataTableRow>
              {#each categoryConfig.props as categoryProp}
                <FeaturedListItem
                  {categoryProp}
                  value={getItemPropValue(item, categoryProp)}
                />
              {/each}
              {#each categoryConfig.details as categoryProp}
                <FeaturedListItem
                  {categoryProp}
                  value={getItemPropValue(item, categoryProp, true)}
                />
              {/each}
              <DataTableCell
                ><Switch
                  checked={false}
                  on:change={() => updateItemStatus(item, ItemStatus.FEATURED)}
                /></DataTableCell
              >
              <DataTableCell>
                <Button icon on:click={() => openItemEdit(item, true)}>
                  <Icon class="mdi mdi-pencil" />
                </Button>
              </DataTableCell>
            </DataTableRow>
          {/each}
        </DataTableBody>
      </DataTable>
    </Scroller>
  </Page>
{/if}

<Dialog width={1280} class="pa-2" bind:active={editDialogActive}>
  <ItemEdit
    isFeatured={true}
    bind:item={itemOnEdit}
    {categoryConfig}
    {isEdit}
    statusAfterEdit={null}
    on:create={wrapProgress(onItemCreate)}
    on:update={wrapProgress(onItemUpdate)}
  />
</Dialog>
